// src/pages/SR_EngineerChecklist.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { sr } from "../api/serviceReports";
import SRSignaturePad from "../components/SR_SignaturePad";

/* -------------------------------- Helpers -------------------------------- */
function getApiBase() {
  if (sr && (sr.baseUrl || sr.API_BASE_URL || sr.apiBase || sr.API)) {
    return sr.baseUrl || sr.API_BASE_URL || sr.apiBase || sr.API;
  }
  return window.API_BASE_URL || "";
}

function extractIds(location) {
  const params = new URLSearchParams(location.search || "");
  const jobId = params.get("job_id") || params.get("jobId");
  const templateId = params.get("template_id") || params.get("templateId");
  const reportId = params.get("report_id") || params.get("reportId"); // ✅ add this
  return {
    jobId: jobId ? Number(jobId) : null,
    templateId: templateId ? Number(templateId) : null,
  };
}

function composeAddress(c) {
  if (!c) return "";
  const parts = [c.address, c.address2, c.address3, c.town, c.province, c.postcode]
    .map((x) => (x || "").trim())
    .filter(Boolean);
  return parts.join(", ");
}

function composeName(c) {
  const first = (c?.first_name || "").trim();
  const sur = (c?.surname || "").trim();
  const email = (c?.email || "").trim();
  return first || sur ? [first, sur].filter(Boolean).join(" ") : email || `Customer #${c?.id ?? ""}`;
}


function makeEquipLabel(eq) {
  const a = (eq?.equipment || "").trim();
  const b = (eq?.description || "").trim();
  const c = (eq?.serial || "").trim();
  const d = (eq?.location || "").trim();
  return [a || b, c, d].filter(Boolean).join(" • ").trim();
}

/* ------------------------------ Image Helpers ----------------------------- */

async function compressImageBlobOrFile(fileOrBlob, opts = {}) {
  const { maxSide = 1600, quality = 0.7 } = opts;
  const blob = fileOrBlob instanceof Blob ? fileOrBlob : new Blob([fileOrBlob]);
  const url = URL.createObjectURL(blob);

  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });

    const w = img.width;
    const h = img.height;
    const scale = Math.min(1, maxSide / Math.max(w, h));
    const nw = Math.max(1, Math.round(w * scale));
    const nh = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement("canvas");
    canvas.width = nw;
    canvas.height = nh;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, nw, nh);

    const out = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", quality)
    );
    return out || blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/* -------------------------------- Component ------------------------------ */





export default function SR_EngineerChecklist() {
  const location = useLocation();
  const API_BASE = getApiBase();
  const { jobId: jobIdFromQuery } = extractIds(location);

  const [engineers, setEngineers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");

  const [customer, setCustomer] = useState("");
  const [address, setAddress] = useState("");
  const [jobRef, setJobRef] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [engineerId, setEngineerId] = useState("");
  const [signature, setSignature] = useState(null);
  const [notes, setNotes] = useState("");

  const [items, setItems] = useState({});
  const [tplTaskMap, setTplTaskMap] = useState({});

  const [equipment, setEquipment] = useState([
    { equipment: "", description: "", serial: "", location: "" },
  ]);

  const addEquipRow = () =>
    setEquipment((rows) => [
      ...rows,
      { equipment: "", description: "", serial: "", location: "" },
    ]);

  const removeEquipRow = (idx) =>
    setEquipment((rows) => rows.filter((_, i) => i !== idx));

  const changeEquip = (idx, field, value) =>
    setEquipment((rows) =>
      rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r))
    );

  const [photos, setPhotos] = useState([]);

  const [reportId, setReportId] = useState(null);

  const [customerEmail, setCustomerEmail] = useState("");

  const [afterSaveOpen, setAfterSaveOpen] = useState(false);
  const [lastPdfUrl, setLastPdfUrl] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  // Ensure the "saved" modal never persists when navigating to this page
  useEffect(() => {
    setAfterSaveOpen(false);
  }, [location?.key]);

  const apiBaseAbs = useMemo(() => {
    const base = (
      API_BASE && /^https?:\/\//i.test(API_BASE) ? API_BASE : window.location.origin
    ).replace(/\/+$/, "");
    return base;
  }, [API_BASE]);

  function pdfUrlFor(id) {
    return `${apiBaseAbs}/api/service-reports/reports/${id}/pdf?ts=${Date.now()}`;
  }

  async function onSelectPhotos(e) {
    const files = Array.from(e.target.files || []);
    const compressed = [];
    for (const f of files) {
      try {
        const blob = await compressImageBlobOrFile(f, {
          maxSide: 1600,
          quality: 0.7,
        });
        const cf = new File([blob], f.name.replace(/\.\w+$/, "") + ".jpg", {
          type: "image/jpeg",
        });
        compressed.push({ file: cf, caption: "", previewUrl: URL.createObjectURL(blob) });
      } catch {
        compressed.push({ file: f, caption: "", previewUrl: URL.createObjectURL(f) });
      }
    }
    setPhotos((prev) => [...prev, ...compressed]);
    e.target.value = "";
  }

  function setPhotoCaption(idx, val) {
    setPhotos((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, caption: val } : p))
    );
  }

  function removePhoto(idx) {
    setPhotos((prev) => {
      const copy = [...prev];
      const [removed] = copy.splice(idx, 1);
      try {
        if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      } catch {}
      return copy;
    });
  }

  async function saveOrUpdateReport(payload) {
    const base = (
      sr?.baseUrl ||
      sr?.API_BASE_URL ||
      sr?.apiBase ||
      sr?.API ||
      window.API_BASE_URL ||
      ""
    ).replace(/\/+$/, "");

    if (reportId) {
      if (sr?.reports?.update) {
        await sr.reports.update(reportId, payload);
        return reportId;
      }
      // fallback REST
      const res = await fetch(`${base}/api/service-reports/reports/${reportId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return reportId;
    }

    if (sr?.reports?.create) {
      const created = await sr.reports.create(payload);
      const id =
        Number(created?.id) ||
        Number(created?.report?.id) ||
        Number(created?.data?.id);
      if (!id) throw new Error("Report created but no id returned.");
      setReportId(id);
      sessionStorage.setItem("sr_current_report_id", String(id));
      return id;
    }

    // fallback REST
    const res = await fetch(`${base}/api/service-reports/reports`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "include",
    });
    if (!res.ok) throw new Error(await res.text());
    const data = await res.json();
    const id = Number(data?.id || data?.report?.id);
    if (!id) throw new Error("Report created but no id returned.");
    setReportId(id);
    sessionStorage.setItem("sr_current_report_id", String(id));
    return id;
  }

  async function sendEmailPdf(id) {
    try {
      setEmailBusy(true);
      const to = (emailTo || "").trim();
      if (!to) {
        alert("Enter an email address first.");
        return;
      }
      const res = await fetch(`${apiBaseAbs}/api/service-reports/reports/${id}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to }),
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      alert("Email sent.");
    } catch (e) {
      console.error(e);
      alert("Failed to send email.");
    } finally {
      setEmailBusy(false);
    }
  }

  /* ------------------------------- Camera bits ------------------------------ */
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const [camStream, setCamStream] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [camBusy, setCamBusy] = useState(false);

  async function openCamera() {
    try {
      setCamBusy(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });
      setCamStream(stream);
      setCamOpen(true);
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (e) {
      alert("Unable to open camera.");
    } finally {
      setCamBusy(false);
    }
  }

  function closeCamera() {
    try {
      camStream?.getTracks()?.forEach((t) => t.stop());
    } catch {}
    setCamStream(null);
    setCamOpen(false);
  }

  async function capturePhoto() {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 1280;
    canvas.height = v.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.8)
    );
    if (!blob) return;

    const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
    const previewUrl = URL.createObjectURL(blob);
    setPhotos((prev) => [...prev, { file, caption: "", previewUrl }]);
  }

  useEffect(
    () => () => {
      photos.forEach((p) => {
        try {
          if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
        } catch {}
      });
      try {
        camStream?.getTracks()?.forEach((t) => t.stop());
      } catch {}
    },
    [] // eslint-disable-line
  );

  /* ----------------------------- Load lists/data ----------------------------- */
  useEffect(() => {
    sr.engineers.list().then((rows) => setEngineers(rows.filter((r) => r.active)));
    sr.tasks.list().then((rows) => setTasks(rows.filter((t) => t.active)));
    sr.templates.list().then((rows) => {
      const actives = rows.filter((t) => t.active);
      setTemplates(actives);
      const { templateId: tFromParams } = extractIds(location);
      if (tFromParams && actives.some((t) => t.id === tFromParams))
        setTemplateId(String(tFromParams));
    });
  }, [location]);

  const [customers, setCustomers] = useState([]);
  const [customerSelect, setCustomerSelect] = useState("manual");
  const isFromCalendarJob = !!jobIdFromQuery;

  useEffect(() => {
    fetch("/api/customers")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((arr) => (Array.isArray(arr) ? setCustomers(arr) : setCustomers([])))
      .catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    if (!templateId) {
      setTplTaskMap({});
      return;
    }
    (async () => {
      try {
        const t = await sr.templates.get(Number(templateId));
        const map = {};
        if (Array.isArray(t?.tasks) && t.tasks.length) {
          for (const x of t.tasks) map[x.task_id] = Number(x.sort_order) || 0;
        } else if (Array.isArray(t?.task_ids)) {
          for (const id of t.task_ids) map[id] = 0;
        }
        setTplTaskMap(map);
      } catch {
        setTplTaskMap({});
      }
    })();
  }, [templateId]);

  useEffect(() => {
    const { jobId } = extractIds(location);
    if (!jobId) return;

    (async () => {
      try {
        const params = new URLSearchParams();
        params.set("job_id", jobId);
        if (templateId) params.set("template_id", Number(templateId));
        const url = `${API_BASE}/api/service-reports/prefill?${params.toString()}`;

        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();

        if (data?.customer?.name) setCustomer(data.customer.name);
        if (data?.customer?.address) setAddress(data.customer.address);
        if (data?.job?.job_ref || data?.job?.quote_number)
          setJobRef(data.job.job_ref || data.job.quote_number);
        if (data?.job?.report_date) setDate(data.job.report_date);
        if (data?.template?.id) setTemplateId(String(data.template.id));

        const next = {};
        (data?.items || []).forEach((it) => {
          next[Number(it.task_id)] = {
            completed: !!it.completed,
            value_text: it.value_text || "",
          };
        });
        if (Object.keys(next).length) setItems(next);

        if (Array.isArray(data?.equipment) && data.equipment.length) {
          setEquipment(
            data.equipment.map((r) => ({
              equipment: (r.equipment ?? r.brand ?? "") || "",
              description: (r.description ?? r.model ?? "") || "",
              serial: r.serial || "",
              location: r.location || "",
            }))
          );
        }

        if (data?.report?.id) {
          const rid = Number(data.report.id);
          if (rid) {
            setReportId(rid);
            sessionStorage.setItem("sr_current_report_id", String(rid));
          }
        }

        setCustomerSelect("manual");
      } catch {}
    })();
  }, [API_BASE, location, templateId]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("SR_PREFILL");
      if (!raw) return;
      const data = JSON.parse(raw);

      if (data?.customer?.name) setCustomer(data.customer.name);
      if (data?.customer?.address) setAddress(data.customer.address);
      if (data?.job?.job_ref || data?.job?.quote_number)
        setJobRef(data.job.job_ref || data.job.quote_number);
      if (data?.job?.report_date) setDate(data.job.report_date);
      if (data?.template?.id) setTemplateId(String(data.template.id));

      const next = {};
      (data?.items || []).forEach((it) => {
        next[Number(it.task_id)] = { completed: !!it.completed, value_text: it.value_text || "" };
      });
      if (Object.keys(next).length) setItems(next);

      if (Array.isArray(data?.equipment) && data.equipment.length) {
        setEquipment(
          data.equipment.map((r) => ({
            equipment: (r.equipment ?? r.brand ?? "") || "",
            description: (r.description ?? r.model ?? "") || "",
            serial: r.serial || "",
            location: r.location || "",
          }))
        );
      }

      if (data?.job?.id) sessionStorage.setItem("sr_job", String(data.job.id));
      if (data?.template?.id) sessionStorage.setItem("sr_template", String(data.template.id));
      sessionStorage.removeItem("SR_PREFILL");
    } catch {}
  }, []);

  function onToggle(taskId, checked) {
    setItems((prev) => ({
      ...prev,
      [taskId]: {
        ...(prev[taskId] || {}),
        completed: checked,
      },
    }));
  }

  function onText(taskId, val) {
    setItems((prev) => ({
      ...prev,
      [taskId]: {
        ...(prev[taskId] || {}),
        value_text: val,
      },
    }));
  }

  function onTextEquip(taskId, equipIdx, val) {
    setItems((prev) => ({
      ...prev,
      [taskId]: {
        ...(prev[taskId] || {}),
        valuesByEquip: {
          ...((prev[taskId] || {}).valuesByEquip || {}),
          [equipIdx]: val,
        },
      },
    }));
  }

  function onPickCustomer(e) {
    const val = e.target.value;
    setCustomerSelect(val);
    if (val === "manual") return;

    const c = customers.find((x) => String(x.id) === String(val));
    if (c) {
      setCustomer(composeName(c));
      setAddress(composeAddress(c));
      setCustomerEmail((c.email || "").trim());
      setEmailTo((prev) => (prev && prev.trim() ? prev : (c.email || "").trim()));
    }
  }

  async function submit() {
    if (!customer.trim() || !address.trim() || !date || !engineerId) {
      alert("Customer, address, date and engineer are required.");
      return;
    }

    // Don't auto-open a blank PDF tab (it can look like a broken/blank page).
    // We'll show an in-page preview modal after saving instead.

    const equipmentClean = equipment
      .map((r) => ({
        equipment: (r.equipment || "").trim(),
        description: (r.description || "").trim(),
        serial: (r.serial || "").trim(),
        location: (r.location || "").trim(),
      }))
      .filter((r) => r.equipment || r.description || r.serial || r.location);

    const multiEquip = equipmentClean.length > 1;

    const payloadItems = Object.entries(items).map(([task_id, v]) => {
      let value_text = null;

      if (multiEquip) {
        const arr = equipmentClean.map((eq, idx) => ({
          label: makeEquipLabel(eq) || `Item ${idx + 1}`,
          value: (v?.valuesByEquip?.[idx] ?? "").toString().trim(),
        }));
        const anyVal = arr.some((x) => x.value !== "");
        value_text = anyVal ? JSON.stringify({ per_equipment: arr }) : null;
      } else {
        const single = (v?.value_text ?? "").toString().trim();
        value_text = single === "" ? null : single;
      }

      return { task_id: Number(task_id), completed: !!v?.completed, value_text };
    });

    const payload = {
      customer_name: customer.trim(),
      site_address: address.trim(),
      job_ref: jobRef.trim() || null,
      report_date: date,
      engineer_id: Number(engineerId),
      signature_base64: signature || null,
      notes: notes.trim() || null,
      template_id: templateId ? Number(templateId) : null,
      items: payloadItems,
      equipment: equipmentClean,
    };

    try {
      const id = await saveOrUpdateReport(payload);

      if (photos.length) {
        const fd = new FormData();
        photos.forEach((p) => fd.append("photos", p.file));
        photos.forEach((p) => fd.append("captions", p.caption || ""));

        const res = await fetch(`${apiBaseAbs}/api/service-reports/reports/${id}/photos`, {
          method: "POST",
          body: fd,
          credentials: "include",
        });

        if (!res.ok) {
          console.warn("Photo upload failed", await res.text());
          alert("Report saved, but photo upload failed.");
        }
      }

      const pdfUrl = pdfUrlFor(id);
      setLastPdfUrl(pdfUrl);
      setAfterSaveOpen(true);

      setEmailTo((prev) => (prev && prev.trim() ? prev : (customerEmail || "").trim()));
    } catch (e) {
      console.error(e);
      alert("There was a problem saving the report.");
    }
  }

  const engineerSelectStyle = {
  color: engineerId ? "inherit" : "#3c3c42", // selected uses theme text colour
  };
  const labelS = { fontSize: 12, fontWeight: 600, marginBottom: 4 };

  const multi = equipment.length > 1;

  const sortedTasks = useMemo(() => {
    const copy = [...tasks];
    copy.sort((a, b) => {
      const sa = tplTaskMap[a.id] ?? 999999;
      const sb = tplTaskMap[b.id] ?? 999999;
      if (sa !== sb) return sa - sb;
      return (a.title || "").localeCompare(b.title || "");
    });
    return copy;
  }, [tasks, tplTaskMap]);

  return (
    <div className="ec-fullbleed">
      <div className="card">
        <style>{`
          /* CustomerForm-style full-bleed without causing mobile left-gap */
          .ec-fullbleed{
            min-height:1px;
            padding:16px clamp(12px,2vw,24px);
            position:relative;
            left:50%;
            right:50%;
            margin-left:calc(50% - 50vw);
            margin-right:calc(50% - 50vw);
            width:100vw;
            max-width:100vw;
            background:var(--card,#0a2a57);
          }
          @media (max-width:640px){
            .ec-fullbleed{
              left:auto;right:auto;
              margin-left:0;margin-right:0;
              width:100%;max-width:100%;
              overflow-x:hidden;
            }
          }

          .sr-row{display:flex;gap:8px;flex-wrap:wrap;}
          .muted{opacity:.8;}

          /* ---- FIX: remove massive gaps between tasks ---- */
          .ec-taskRow{
            display:flex;
            gap:4px;
            align-items:flex-start;
            padding:6px 0;
            margin:0;
            border-bottom:1px solid rgba(255,255,255,0.08);
          }
          .ec-check{padding-top:2px;}
          .ec-taskTitle{
            flex:1 1 320px;
            min-width:220px;
            padding-top:2px;
            overflow-wrap:anywhere;
          }
          .ec-taskText{
            flex:1 1 340px;
            min-width:220px;
            min-height:1px;
          }

          /* Per-equipment row layout */
          .ec-equipRow{
            display:grid;
            grid-template-columns:160px 1fr;
            gap:2px;
            align-items:center;
            margin:0 0 2px 0;
            min-width:0;
          }
          .ec-equipLabel{
            font-size:12px;
            color:#bbb;
            overflow-wrap:anywhere;
          }

          /* Mobile: stack input under title so it never disappears */
          @media (max-width:980px){
            .ec-taskRow{gap:4px;}
            .ec-taskTitle,.ec-taskText{min-width:0;width:100%;}
            .ec-equipRow{grid-template-columns:1fr;}
          }
        `}</style>

        <div className="sr-row" style={{ alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ margin: 0 }}>Engineer Checklist</h2>

          {reportId ? (
            <div className="muted" style={{ fontSize: 12 }}>
              Editing report #{reportId}
            </div>
          ) : (
            <div className="muted" style={{ fontSize: 12 }}>
              New report
            </div>
          )}
        </div>

        {/* Customer selection */}
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Customer</h3>

          {customers.length ? (
            <div className="sr-row" style={{ marginBottom: 10 }}>
              <select className="input" value={customerSelect} onChange={onPickCustomer}>
                <option value="manual">Manual entry</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {composeName(c)} {c.postcode ? `(${c.postcode})` : ""}
                  </option>
                ))}
              </select>
              <div className="muted" style={{ fontSize: 12, paddingTop: 10 }}>
                {isFromCalendarJob ? "Prefilled from job (if available)." : ""}
              </div>
            </div>
          ) : null}

          <div className="sr-row" style={{ marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={labelS}>Customer name</div>
              <input className="input" placeholder="Customer" value={customer} onChange={(e) => setCustomer(e.target.value)} />
            </div>
            <div style={{ flex: 2, minWidth: 220 }}>
              <div style={labelS}>Site address</div>
              <input className="input" placeholder="Address" value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
          </div>

          <div className="sr-row" style={{ marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={labelS}>Job ref (optional)</div>
              <input className="input" placeholder="Job ref" value={jobRef} onChange={(e) => setJobRef(e.target.value)} />
            </div>
            <div>
              <div style={labelS}>Date</div>
              <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={labelS}>Engineer</div>
              <select
                className="input"
                value={engineerId}
                onChange={(e) => setEngineerId(e.target.value)}
                style={engineerSelectStyle}
              >
                <option value="" style={{ color: "#3c3c42" }}>
                  Select engineer…
                </option>
                {engineers.map((e) => (
                  <option key={e.id} value={e.id} style={{ color: "#3c3c42" }}>
                    {e.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <h3>Equipment</h3>

          {equipment.map((row, idx) => (
            <div
              key={idx}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 8,
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <input className="input" placeholder="Equipment / Brand" value={row.equipment} onChange={(e) => changeEquip(idx, "equipment", e.target.value)} />
              <input className="input" placeholder="Description / Model" value={row.description} onChange={(e) => changeEquip(idx, "description", e.target.value)} />
              <input className="input" placeholder="Serial #" value={row.serial} onChange={(e) => changeEquip(idx, "serial", e.target.value)} />

              <div style={{ display: "flex", gap: 8 }}>
                <input className="input w-full" placeholder="Location" value={row.location} onChange={(e) => changeEquip(idx, "location", e.target.value)} />
                {equipment.length > 1 && (
                  <button type="button" className="btn btn-danger px-2" onClick={() => removeEquipRow(idx)}>
                    –
                  </button>
                )}
              </div>
            </div>
          ))}

          <div className="sr-row" style={{ marginTop: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={addEquipRow}>
              + Add equipment
            </button>

            <div className="muted" style={{ fontSize: 12, paddingTop: 10 }}>
              {multi ? "Multi-equipment mode: each task can store per-equipment readings." : ""}
            </div>
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <div className="sr-row" style={{ alignItems: "center", justifyContent: "space-between" }}>
            <h3 style={{ margin: 0 }}>Checklist</h3>
            <div style={{ display: "flex", gap: 8 }}>
              <select className="input" value={templateId} onChange={(e) => setTemplateId(e.target.value)} style={{ minWidth: 220 }}>
                <option value="">All active tasks</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginTop: 8 }}>
            {sortedTasks.map((t) => {
              const val = items[t.id] || {};
              return (
                <div key={t.id} className="ec-taskRow">
                  <div className="ec-check">
                    <input type="checkbox" checked={!!val.completed} onChange={(e) => onToggle(t.id, e.target.checked)} />
                  </div>

                  <div className="ec-taskTitle">
                    <div style={{ fontWeight: 600 }}>{t.title || t.name}</div>
                    {t.description ? (
                      <div className="muted" style={{ fontSize: 12 }}>
                        {t.description}
                      </div>
                    ) : null}
                  </div>

                  <div className="ec-taskText">
                    {multi ? (
                      <div>
                        {equipment.map((eq, idx) => (
                          <div key={idx} className="ec-equipRow">
                            <div className="ec-equipLabel">{makeEquipLabel(eq) || `Item ${idx + 1}`}</div>
                            <input
                              className="input"
                              placeholder={t?.placeholder || "Enter value…"}
                              value={(val?.valuesByEquip?.[idx] ?? "").toString()}
                              onChange={(e) => onTextEquip(t.id, idx, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <input
                        className="input"
                        placeholder={t?.placeholder || "Enter value…"}
                        value={(val.value_text ?? "").toString()}
                        onChange={(e) => onText(t.id, e.target.value)}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <h3>Photos</h3>

          <div className="sr-row" style={{ marginBottom: 10 }}>
            <button type="button" className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>
              Upload photos
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={onSelectPhotos}
            />
          </div>

          {camOpen && (
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="sr-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 600 }}>Camera</div>
                <button type="button" className="btn btn-danger" onClick={closeCamera}>
                  Close
                </button>
              </div>
              <div style={{ marginTop: 10 }}>
                <video ref={videoRef} autoPlay playsInline style={{ width: "100%", borderRadius: 10 }} />
              </div>
              <div className="sr-row" style={{ marginTop: 10 }}>
                <button type="button" className="btn btn-primary" onClick={capturePhoto}>
                  Capture photo
                </button>
              </div>
            </div>
          )}

          {photos.length ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
              {photos.map((p, idx) => (
                <div key={idx} className="card" style={{ padding: 10 }}>
                  <img src={p.previewUrl} alt="" style={{ width: "100%", borderRadius: 6 }} />
                  <input
                    className="input"
                    placeholder="Caption (optional)"
                    style={{ marginTop: 8 }}
                    value={p.caption || ""}
                    onChange={(e) => setPhotoCaption(idx, e.target.value)}
                  />
                  <button type="button" className="btn btn-danger" style={{ marginTop: 8 }} onClick={() => removePhoto(idx)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="muted" style={{ fontSize: 12 }}>
              No photos added.
            </div>
          )}
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <h3>Signature</h3>
          <SRSignaturePad value={signature} onChange={setSignature} />
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <h3>Notes</h3>
          <textarea className="input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes…" />
        </div>

        <div className="sr-row" style={{ marginTop: 16 }}>
          <button type="button" className="btn btn-primary" onClick={submit}>
            {reportId ? "Update report & preview PDF" : "Save report & preview PDF"}
          </button>
        </div>

        {afterSaveOpen && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2147483647,
              padding: 16,
            }}
            onClick={() => setAfterSaveOpen(false)}
          >
            <div className="card" style={{ width: "min(720px, 100%)", padding: 16 }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0 }}>Report saved</h3>

{lastPdfUrl ? (
  <div style={{ marginTop: 12 }}>
    <div className="sr-row" style={{ marginTop: 12, flexWrap: "wrap" }}>
      <a
        className="btn btn-secondary"
        href={lastPdfUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}
      >
        View PDF report
      </a>

      <button type="button" className="btn btn-secondary" onClick={() => setAfterSaveOpen(false)}>
        Back to checklist
      </button>

      <div style={{ flex: "1 1 240px" }} />

      <input
        className="input"
        value={emailTo}
        onChange={(e) => setEmailTo(e.target.value)}
        placeholder="Email to…"
        style={{ minWidth: 240 }}
      />

      <button
        type="button"
        className="btn btn-success"
        disabled={emailBusy || !reportId || !(emailTo || "").trim()}
        onClick={() => reportId && sendEmailPdf(reportId)}
      >
        {emailBusy ? "Sending…" : "Send email"}
      </button>
    </div>
  </div>
) : (
  <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
    PDF link not available yet.
  </div>
)}

            </div>
          </div>
        )}
      </div>
    </div>
  );
}
